import React, { useState, useEffect } from "react";
import apiService from "../../config";
import { useLanguage } from '../../StoreContext/LanguageContext';


const EditRailwayProposal = ({ projectName, projectId, proposalId, onBack }) => {
  const { language, translations } = useLanguage();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [signatureFile, setSignatureFile] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [areaUnits, setAreaUnits] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');

  // Load existing proposal data
  useEffect(() => {
    if (proposalId) {
      loadProposalData();
    }
  }, [proposalId]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (formData.dist_name && districts.length > 0 && talukas.length === 0) {
      loadTalukas(formData.dist_name);
    }
  }, [formData, districts, talukas.length]);

  const loadProposalData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/getDashboardRailwayProposal?projectId=${projectId}&proposalId=${proposalId}`);
      if (response && response.status === 200) {
        let data = response.data || {};

        // Fields that should be shown as "हो" / "नाही"
        const fieldsToConvert = [
          "schedule_6_08",
          "schedule_4_09",
          "extracts_7_12_10",
          "drone_kml",
          "demarcation_11",
          "acquisition_matter_12",
          "land_acquisition_type_13",
          "excess_land_reason_14",
          "other_govt_land_15",
          "near_religious_places_16",
          "public_route_obstruction_17",
          "near_railway_boundary_18",
          "road_obstruction_19",
          "private_buildings_20",
          "expenses_22",
          "financial_provision_23",
          "acquisition_permanent_temporary",
          "institutional_resolution_21",
          "cancellation_cost_commitment_22",
          "financial_provision_confirmation_23",
        ];

        // Convert 1/0, true/false to "हो"/"नाही"
        fieldsToConvert.forEach(field => {
          if (data[field] === 1 || data[field] === true || data[field] === "1") {
            data[field] = "होय";
          } else if (data[field] === 0 || data[field] === false || data[field] === "0") {
            data[field] = "नाही";
          }
        });

        setFormData(data);

        // If districts are already loaded, trigger dependent dropdowns
        if (data.dist_name && districts.length > 0) {
          loadTalukas(data.dist_name);
          if (data.taluka_name) {
            setTimeout(() => {
              loadVillages(data.dist_name, data.taluka_name);
            }, 500);
          }
        }
      }
    } catch (err) {
      console.error("Error loading proposal data:", err);
      setError("Failed to load proposal data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear success message when user starts editing
    if (success) setSuccess(false);
  };

  const handleViewFile = (fieldName) => {
    const fileUrl = formData[`${fieldName}_file`];
    console.log("🔎 handleViewFile called for:", fieldName, "=>", fileUrl);

    if (!fileUrl) {
      console.warn("⚠️ No file found for field:", fieldName);
      return;
    }

    // Detect File object vs URL string
    if (fileUrl instanceof File) {
      console.log("📂 Detected a File object:", fileUrl.name);
      const url = URL.createObjectURL(fileUrl);
      console.log("👉 Created blob URL:", url);
      setDocumentUrl(url);
    } else {
      console.log("🌐 Detected a string URL:", fileUrl);
      setDocumentUrl(fileUrl);
    }

    setShowModal(true);
    console.log("✅ Modal state set to true");
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // Handle ISO date format from database (2025-08-01T00:00:00.000Z)
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      if (day && month && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle DD-MM-YYYY format
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[2].length === 4) {
        // DD-MM-YYYY format
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else if (parts.length === 3 && parts[0].length === 4) {
        // Already in YYYY-MM-DD format
        return dateString;
      }
    }

    return '';
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (optional)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('कृपया फक्त JPG, PNG किंवा PDF फाइल अपलोड करा');
        return;
      }

      // Validate file size (optional - 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('फाइल साइझ 5MB पेक्षा कमी असावी');
        return;
      }

      // Set the signature file
      setSignatureFile(file);

      // Create preview URL for images (not for PDFs)
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl(null);
      }

      console.log('Signature file uploaded:', file.name);
    }
  };


  // Updated formatDateForDatabase function
  const formatDateForDatabase = (dateString) => {
    if (!dateString) return null;

    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      if (day && month && year && year.length === 4) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle DD-MM-YYYY format
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[2].length === 4) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle ISO format
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    return null;
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [`${fieldName}_file`]: file
      }));
      console.log('File uploaded for:', fieldName, file.name);
    }
  };

  const loadDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await apiService.getDistricts();
      if (response && response.status === 200) {
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      setError('जिल्हे लोड करण्यात अयशस्वी');
    } finally {
      setLoadingDistricts(false);
    }
  };

  const loadTalukas = async (districtName) => {
    if (!districtName) {
      setTalukas([]);
      setVillages([]);
      return;
    }

    setLoadingTalukas(true);
    try {
      const response = await apiService.getTalukas(districtName);
      if (response && response.status === 200) {
        setTalukas(response.data || []);
      }
    } catch (error) {
      console.error('Error loading talukas:', error);
      setError('तालुके लोड करण्यात अयशस्वी');
    } finally {
      setLoadingTalukas(false);
    }
  };

  const loadVillages = async (districtName, talukaName) => {
    if (!districtName || !talukaName) {
      setVillages([]);
      return;
    }

    setLoadingVillages(true);
    try {
      const response = await apiService.getVillages(districtName, talukaName);
      if (response && response.status === 200) {
        setVillages(response.data || []);
      }
    } catch (error) {
      console.error('Error loading villages:', error);
      setError('गावे लोड करण्यात अयशस्वी');
    } finally {
      setLoadingVillages(false);
    }
  };
  // Load Area Units
  const loadAreaUnits = async () => {
    try {
      const response = await apiService.getAreaUnits();
      if (response && response.status === 200) {
        setAreaUnits(response.data || []);
      }
    } catch (error) {
      console.error('Error loading area units:', error);
      setError('क्षेत्रफळ युनिट लोड करण्यात अयशस्वी');
    }
  };
  const handleDistrictChange = (value) => {  // Change parameter
    setFormData(prev => ({
      ...prev,
      dist_name: value,
      taluka_name: '',
      village_name: ''
    }));

    setTalukas([]);
    setVillages([]);

    if (value) {
      loadTalukas(value);
    }
  };

  const handleTalukaChange = (value) => {  // Change parameter
    setFormData(prev => ({
      ...prev,
      taluka_name: value,
      village_name: ''
    }));

    setVillages([]);

    if (formData.dist_name && value) {
      loadVillages(formData.dist_name, value);
    }
  };

  const handleVillageChange = (value) => {  // Change parameter
    setFormData(prev => ({
      ...prev,
      village_name: value
    }));
  };

  useEffect(() => {
    loadDistricts();
    loadAreaUnits();
  }, []);

  // Fix the useEffect that handles form data loading
  useEffect(() => {
    // Load talukas and villages based on existing form data when editing
    if (formData.dist_name && districts.length > 0) {
      // Load talukas for the selected district
      loadTalukas(formData.dist_name);

      if (formData.taluka_name) {
        // Load villages for the selected district and taluka
        setTimeout(() => {
          loadVillages(formData.dist_name, formData.taluka_name);
        }, 300);
      }
    }
  }, [formData.dist_name, formData.taluka_name, districts]);

    const getSrNo = (idx) => idx + 1;


  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      const convertToBitValue = (value) => {
        if (value === "होय") return "1";
        if (value === "नाही") return "0";
        return value;
      };

      const fieldsToConvert = [
        "schedule_6_08",
        "schedule_4_09",
        "extracts_7_12_10",
        "demarcation_11",
        "acquisition_matter_12",
        "land_acquisition_type_13",
        "excess_land_reason_14",
        "other_govt_land_15",
        "near_religious_places_16",
        "public_route_obstruction_17",
        "near_railway_boundary_18",
        "road_obstruction_19",
        "private_buildings_20",
        "institutional_resolution_21",
        "cancellation_cost_commitment_22",
        "financial_provision_confirmation_23",
        "expenses_22",
        "financial_provision_23"
      ];

      const dateFields = [
        "acquisition_proposal_date",
        "administrative_order_date"
      ];

      const allowedFields = [
        "acquisition_proposal_no",
        "acquisition_proposal_date",
        "propsal_description",
        "administrative_order_no",
        "administrative_order_date",
        "acquisition_incharge_name",
        "acquisition_incharge_designation",
        "acquisition_incharge_address",
        "acquisition_agency",
        "acquisition_department",
        "acquisition_purpose",
        "acquisition_government_policy",
        "village_name",
        "taluka_name",
        "dist_name",
        "area_unit",
        "total_area",
        "aquired_area",
        "acquisition_regional_incharge_name",
        "acquisition_regional_incharge_designation",
        "acquisition_regional_incharge_email",
        "acquisition_regional_incharge_contact",
        "project_description",
        "project_social_benfit",
        "displacement_reason",
        "social_impact",
        ...fieldsToConvert
      ];

      Object.keys(formData).forEach(key => {
        if (allowedFields.includes(key)) {
          if (key.endsWith('_file')) {
            // This is a file
            formDataToSend.append(key, formData[key]);
          } else {
            let value = formData[key];

            // Handle date fields
            if (dateFields.includes(key)) {
              value = formatDateForDatabase(value);
              if (value === null) {
                // Skip invalid dates
                return;
              }
            }
            // Convert bit fields
            else if (fieldsToConvert.includes(key)) {
              value = convertToBitValue(value);
            }

            formDataToSend.append(key, value || '');
          }
        }
      });

      // Add uploaded files
      Object.keys(uploadedFiles).forEach(key => {
        formDataToSend.append(key, uploadedFiles[key]);
      });

      console.log('Updating proposal with fields:', Array.from(formDataToSend.keys()));

      const response = await apiService.patch(
        `/patchUpdateRailwayProposal?proposalId=${proposalId}`,
        formDataToSend
      );

      if (response && response.status === 200) {
        setSuccess(true);
        alert("प्रस्ताव सफळतापूर्वक अद्यतन केला गेला!");
        // loadProposalData();
        window.location.reload();
      } else {
        setError(response?.message || "Update failed");
      }
    } catch (err) {
      console.error("Error updating proposal:", err);
      setError(err.message || "Failed to update proposal");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const printWindow = window.open('', '_blank');

    // Create print content matching the image format
    const createPrintContent = () => {
      return `
        <div class="font-sans pt-4">
            <!-- Header -->
            
            <div class="text-center mb-2">
            <p class="text-xs m-0 leading-tight mb-5 text-center">
            प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनींचा तपशील
                </p>
                <h2 class="mb-2 text-center text-base font-bold">
                   प्रकल्प नाव: ${projectName || 'N/A'} | प्रस्ताव नाव/नाव: ${formData.acquisition_proposal_no || "N/A"}
                </h2>
                <p class="text-xs leading-tight text-center">
                    मौजे : ${formData.village_name || "N/A"} , तालुका : ${formData.taluka_name || "N/A"} , जिल्हा : ${formData.dist_name || "N/A"}
                </p>
                <h2 class="mb-3 text-base font-bold">
                    नमुना "क"
                </h2>
                <p class="text-xs m-0 leading-tight mb-2 text-center">
                    ${projectName || 'N/A'} साठी खाजगी जमिनीच्या भूसंपादचा प्रस्ताव ${formData.acquisition_proposal_no || "N/A"}
                </p>
               
            </div>
            
            <table class="w-full border-collapse border-2 border-black text-xs">
                <tbody>
                    <!-- १ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">
                            १
                        </td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            प्रस्ताव पाठविणाऱ्या अधिकाऱ्याचे पदनाम व पत्ता
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">नाव: </span>
                                    <span class="break-words">${formData.acquisition_incharge_name || "N/A"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">पदनाम/संपादन संस्थेचे नाव: </span>
                                    <span class="break-words">${formData.acquisition_incharge_designation || "-"}</span>
                                </div>
                                  <div>
                                    <span class="font-semibold">पत्ता: </span>
                                    <span class="break-words">${formData.acquisition_incharge_address || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!-- २ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">२</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           संपादन संस्थेचे नाव व पत्ता
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_agency || "-"}
                        </td>
                    </tr>

                    <!-- ३ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">३</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            मंमंत्रालयीन स्तरावरील संबंधित प्रशासकीय विभाग
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_department || "-"}
                        </td>
                    </tr>

                    <!-- ४ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">४</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           जमीन कोणत्या प्रयोजनासाठी संपादन करून हवी आहे
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_purpose || "-"}
                        </td>
                    </tr>

                    <!-- ५ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">५</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                      जमीन संपादन कोणत्या कायदयाखाली करून हवी आहे
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_government_policy || "-"}
                        </td>
                    </tr>

                    <!-- ६ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">६</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन क्षेत्रात मोडणारी गावे, तालुका व जिल्हा कोणता?
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">जिल्हा: </span>
                                    <span class="break-words">${formData.dist_name || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">तालुका: </span>
                                    <span class="break-words">${formData.taluka_name || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">गाव: </span>
                                    <span class="break-words">${formData.village_name || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!-- ७ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">७</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करून हवे असलेले एकूण क्षेत्र
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">एकूण क्षेत्र: </span>
                                    <span class="break-words">${formData.total_area || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">संपादन क्षेत्र: </span>
                                    <span class="break-words">${formData.aquired_area || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">क्षेत्रफळ युनिट: </span>
                                    <span class="break-words">${formData.area_unit || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!-- ८ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">८</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        गावनिहाय व सर्व क्रमांकानुसार आणि संपादन क्षेत्राच्या चर्तुःसिमा दाखविणारी या सोबतच्या नमून्यातील अनुसूची यासोबत जोडणे आवश्यक आहे त्याप्रमाणे ती जोडली आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.schedule_6_08 ? "होय" : "नाही"}
                        </td>
                    </tr>

                    <!-- ९ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">९</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        संपादन करावयाच्या क्षेत्राचा चित्रांकित साक्षांकित नकाशा यासोबत जोडाणे आवश्यक आहे त्याप्रमाणे तो जोडला आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.schedule_4_09 ? "होय" : "नाही"}
                        </td>
                    </tr>

                  <!-- १०अ (Rowspan) -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१०</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        संपादन करावयाच्या क्षेत्राचे ७/१२ उतारे किंवा मालमत्ता नोंदवहीतील उतारे यासोबत जोडणे आवश्यक आहे त्याप्रमाणे ते जोडले आहेत काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.extracts_7_12_10 || "नाही"}
                        </td>
                    </tr>
                    <!-- ११ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">११</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन क्षेत्राचे सिमांकन करण्यात आले आहे काय ? असल्यास सिमांकन प्रमाणपत्र सोबत जोडले आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.demarcation_11 ? "होय" : "नाही"}
                        </td>
                    </tr>

                    <!-- १२ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१२</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        संपादन करावयाच्या संपूर्ण जमिनीचा वा तिच्या काही भागाचा खाजगीरित्या ताबा घेतला आहे काय? असल्यास, केव्हा व त्याबाबतीत भुसंपादन नियम पुस्तिकेच्या परिच्छेद २१ अन्वये परिशिष्ट २६ प्रमाणे पंचनामा व करार केला आहे काय? असल्यास, त्याची प्रत सोबत जोडावी.
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.acquisition_matter_12 ? "होय" : "नाही"}
                        </td>
                    </tr>

                    <!-- १३ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१३</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                          संपादन कायम स्वरूपाचे किंवा तात्पुरते आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            <pre class="whitespace-pre-wrap break-words">${formData.land_acquisition_type_13 || "-"}</pre>
                        </td>
                    </tr>

                    <!-- १४ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१४</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे की जरूरीपेक्षा जास्त क्षेत्राची मागणी केली आहे? असल्यास किती जास्त क्षेत्राची व का?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.excess_land_reason_14 ? "होय" : "नाही"}
                        </td>
                    </tr>

                    <!-- १५ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१५ </td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           संबंधित प्रयोजनासाठी जवळपास कमी खर्चाची वा सरकारी जमिन उपलब्ध नाही काय ? असल्यास संपादन करून मागितलेलीच जमीन का हवी आहे? 
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.other_govt_land_15 || "-"}
                        </td>
                    </tr>

                    <!-- १६ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१६</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           संपादन क्षेत्रात, धार्मिक प्रार्थना स्थळे वा मंदीरे, कबरस्थान वा स्मशानभूमी किंवा प्राचीन स्मारके येतात काय? किंवा ती संपादन क्षेत्राच्या जवळपास असल्यास किती अंतरावर आहेत ? 
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.near_religious_places_16 || "-"}
                        </td>
                    </tr>

           <!-- १७अ (Rowspan) -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१७</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                          संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकारी जलप्रवाह, जलवाहिनी किंवा जल:निसारण मार्ग आहे काय किंवा त्यात काही अडथळा निर्माण होतो काय? 
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.public_route_obstruction_17 || "-"}
                        </td>
                    </tr>
                    

                    <!-- १८ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१८</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करून मागितलेलीच जमीन रेल्वे सिमांच्या जवळपास आहे काय? असल्यास किती अंतरावर आहे.
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.near_railway_boundary_18 || "-"}
                        </td>
                    </tr>

                    <!-- १९ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">१९</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाच किंवा हवेचे मार्ग, यावरील कोणत्याही अधिकारात अडथळा येण्याचा संभव आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.road_obstruction_19 || "-"}
                        </td>
                    </tr>

                    <!-- २०-->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">२०</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           संपादन क्षेत्राची सीमा व इमारत किंवा कारखाना यांना किंवा यांची जागा छेदून आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.private_buildings_20 ? "होय" : "नाही"}
                        </td>
                    </tr>

                    <!-- २१ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">२१</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            भुसंपादनाचा प्रस्ताव कोणत्याही निमशासकीय वा शासकीय वा स्थानिक स्वराज्य संस्थेकडून उदा. महानगरपालिका, महामंडळे इत्यादीकरिता असल्यास असे संपादन करण्याबाबतीत आवश्यक तो ठराव मंजूर झाला आहे काय? असल्यास त्याची प्रत जोडावी.
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold"></span>
                                    <span class="break-words">${formData.institutional_resolution_21 || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!-- २२ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">२२</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           जर भुसंपादन मध्येच रद्द करण्यात आल्यास तो पर्यंतच्या काळात भुसंपादनाविषयक झालेला सर्व आस्थापना खर्च नियमानुसार देण्याची संपादन संस्थांची तयारी आहे काय?
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold"></span>
                                    <span class="break-words">${formData.cancellation_cost_commitment_22 || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!-- २३ -->
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">२३</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                          संपादीत जमिनीची नुकसान भरपाई रक्कम आणि भुसंपादन विषयक सर्व आस्थापना खर्चाची रक्कम यांची आर्थिक तरतूद केलेली आहे काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold"></span>
                                    <span class="break-words">${formData.financial_provision_confirmation_23 || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="mt-8">
                        <p className="text-sm">
                        <span className="font-bold text-sm">टिप:- खालील बाबी जोडणे आवश्यक आहे.</span><br />                         
                        1. अनुक्रमांक ८ मध्ये उल्लेखिलेल्याप्रमाणे चित्रांकित अनुसूची.<br/>
                        2. अनुक्रमांक ९ मध्ये उल्लेखिलेल्याप्रमाणे चित्रांकित/ साक्षांकित नकाशा <br/>
                        3. अनुक्रमांक ११ मध्ये उल्लेखिलेल्याप्रमाणे सिमांकन प्रमाणपत्र
                        </p>
                        </div>
                        
                        <div style="width:100%; margin-top:40px; font-size:14px;">

                        <!-- Left + Right container -->
                        <div style="display:flex; justify-content:space-between; align-items:flex-start;">

                          <!-- Left side : Place -->
                          <div style="text-align:left;">
                            <strong>ठिकाण :</strong> ${formData.village_name || "-"}
                          </div>

                          <!-- Right side : Signature -->
                          <div style="text-align:center; min-width:320px;">
                            
                            <div style="margin-bottom:40px;">
                              संपादन संस्थेच्या जबाबदार अधिकाऱ्याची स्वाक्षरी व शिक्का
                            </div>

                            <div>
                              <p style="margin:0;">उपकार्यकारी अभियंता, (बांधकाम विभाग),</p>
                              <p style="margin:0;">दक्षिण मध्य रेल्वे, अकोला</p>
                            </div>

                          </div>

                        </div>

</div>

                        
              
    </div>
  `;
    };

    const printHTML = `
    <!DOCTYPE html>
    <html>
    <head>
<title>नमुना "क" - ${projectName || ''} - ${formData.proposalId || ''}</title>      <meta charset="UTF-8">
      <style>
         @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
        table {
          page-break-inside: auto;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        .border {
          border: 1px solid black;
        }
        .border-2 {
          border-width: 2px;
        }
        .border-black {
          border-color: black;
        }
        .border-collapse {
          border-collapse: collapse;
        }
        .w-full {
          width: 100%;
        }
        .text-xs {
          font-size: 0.75rem;
        }
        .text-sm {
          font-size: 0.875rem;
        }
        .text-base {
          font-size: 1rem;
        }
        .font-bold {
          font-weight: bold;
        }
        .font-medium {
          font-weight: 500;
        }
        .font-semibold {
          font-weight: 600;
        }
        .px-4 {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        .py-3 {
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        .mb-5 {
          margin-bottom: 1.25rem;
        }
        .pt-4 {
          padding-top: 1rem;
        }
        .text-center {
          text-align: center;
        }
        .align-top {
          vertical-align: top;
        }
        .break-words {
          word-wrap: break-word;
        }
        .leading-tight {
          line-height: 1.25;
        }
        .m-0 {
          margin: 0;
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .whitespace-pre-wrap {
          white-space: pre-wrap;
        }
        @media print {
          body { margin: 0; }
          button { display: none !important; }
          input[type="file"] { display: none !important; }
          select { display: none !important; }
          input { display: none !important; }
          textarea { display: none !important; }
        }
      </style>
    </head>
    <body>
      ${createPrintContent()}
    </body>
    </html>
  `;

    // Write the HTML and trigger print
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.focus();

    // Add a small delay before printing to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };


  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '16px'
      }}>
        प्रस्ताव माहिती लोड करत आहे...
      </div>
    );
  }

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      fontFamily: "Arial, sans-serif",
      maxWidth: "800px",
      margin: "0 auto"
    }}>
      {/* Header with back button */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        paddingBottom: "10px",
        borderBottom: "2px solid black"
      }}>
        <h2 style={{ margin: "0"}}>
          प्रस्ताव संपादन
        </h2>
        {onBack && (
          <button
            onClick={onBack}
            className="no-print"
            style={{
              backgroundColor: "#757575",
              color: "white",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer"
            }}
          >
            ← {translations[language]?.back}
          </button>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "20px"
      }}>
        <button
          onClick={handleDownload}
          style={{
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "10px 20px",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          📄{translations[language]?.Print}
        </button>
        <div style={{ position: "relative" }}>
          <input
            type="file"
            id="signatureUpload"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleSignatureUpload}
            style={{ display: "none" }}
          />
          <button
            onClick={() => document.getElementById('signatureUpload').click()}
            style={{
              backgroundColor: "#FF9800",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            ✍️ {translations[language]?.UploadSignature}
          </button>
          {signatureFile && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: "0",
              marginTop: "5px",
              padding: "5px 10px",
              backgroundColor: "#e8f5e8",
              border: "1px solid #4caf50",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#2e7d32",
              whiteSpace: "nowrap",
              zIndex: 10
            }}>
              ✓ {signatureFile.name}
            </div>
          )}
        </div>
      </div>

      {/* Proposal Info */}
      <div style={{
        padding: "10px",
        borderRadius: "4px",
        marginBottom: "20px",
        fontSize: "12px", textAlign: "center",

      }}>
      <p class="text-sm m-0 leading-tight mb-2 text-center">
         प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनींचा तपशील
        </p>
      <h2 style={{ marginBottom: "10px", fontSize: "18px", fontWeight: "bold" }}>
          प्रकल्प नाव: {projectName || 'N/A'} | प्रस्ताव नाव/नाव: {formData.acquisition_proposal_no || "-"}
        </h2>
      <p class="text-sm m-0 leading-tight mb-2 text-center">
          मौजे : {formData.village_name || "-"} , तालुका : {formData.taluka_name || "-"} , जिल्हा : {formData.dist_name || "-"}
        </p>
        <h2 style={{ marginBottom: "10px", fontSize: "18px", fontWeight: "bold" }}>
          नमुना "क"
        </h2>
        <p class="text-sm m-0 leading-tight text-center">
          {projectName || 'N/A'} साठी खाजगी जमिनीच्या भूसंपादचा प्रस्ताव {formData.acquisition_proposal_no || "-"}
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          backgroundColor: "#e8f5e8",
          color: "#2e7d32",
          padding: "10px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #4caf50"
        }}>
          ✓ प्रस्ताव सफळतापूर्वक अद्यतन केला गेला!
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: "#ffebee",
          color: "#c62828",
          padding: "10px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #ef5350"
        }}>
          {error}
        </div>
      )}

      {/* Form Table */}
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        border: "2px solid #000",
        fontSize: "12px"
      }}>
        <thead>
          <tr>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "8%",
              textAlign: "center"
            }}>
              अनुक्रमांक
            </th>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "45%"
            }}>
              फील्ड नाव / Field Name
            </th>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "47%"
            }}>
              मूल्य / Value
            </th>
          </tr>
        </thead>
        <tbody>
          {formFields.map((field, idx) => (
            <tr key={idx}>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "center",
                    verticalAlign: "top"
                  }}
                  
                >
                  {getSrNo(idx)}
                </td>
              
              <td style={{
                border: "1px solid #000",
                padding: "8px",
                verticalAlign: "top",
                fontWeight: "bold"
              }}>
                {field.label}
                {field.required && <span style={{ color: "red" }}> *</span>}
              </td>
              <td style={{
                border: "1px solid #000",
                padding: "8px",
                verticalAlign: "top"
              }}>
                {field.type === "text" && (
                  <input
                    type="text"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}

                {field.type === "dual_text" && (
                  <div>
                    <div style={{ marginBottom: "8px" }}>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field1Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field1Name] || ""}
                        onChange={(e) => handleInputChange(field.field1Name, e.target.value)}
                        placeholder={field.field1Placeholder}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field2Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field2Name] || ""}
                        onChange={(e) => handleInputChange(field.field2Name, e.target.value)}
                        placeholder={field.field2Placeholder}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field3Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field3Name] || ""}
                        onChange={(e) => handleInputChange(field.field3Name, e.target.value)}
                        placeholder={field.field3Placeholder}
                      />
                    </div>
                  </div>

                )}

                {field.type === "textarea" && (
                  <textarea
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      minHeight: "60px",
                      resize: "vertical",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                    rows={field.rows || 3}
                  />
                )}

                {field.type === "date" && (
                  <input
                    type="date"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formatDateForInput(formData[field.fieldName]) || ""}
                    onChange={(e) => {
                      // Store the date value directly from the date input
                      handleInputChange(field.fieldName, e.target.value);
                    }}
                  />
                )}

                {field.type === "select" && (
                  <>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <select
                        style={{
                          flex: "1",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.fieldName] || ""}
                        onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                      >
                        <option value="">निवडा</option>
                        {field.options.map((option, i) => (
                          <option key={i} value={option.value}>{option.label}</option>
                        ))}
                      </select>

                      {/* View Button - Show when value is "हो" and file URL exists */}
                      {formData[field.fieldName] === "होय" && formData[`${field.fieldName}_file`] && (
                        <button
                          type="button"
                          onClick={() => handleViewFile(field.fieldName)}
                          style={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            padding: "4px 8px",
                            border: "none",
                            borderRadius: "2px",
                            fontSize: "11px",
                            cursor: "pointer",
                            whiteSpace: "nowrap"
                          }}
                        >
                          👁️ पहा
                        </button>
                      )}
                    </div>

                    {/* Show file upload only if value is "हो" and showUpload is true */}
                    {field.showUpload && formData[field.fieldName] === "होय" && (
                      <div style={{ marginTop: "8px" }}>
                        <input
                          type="file"
                          onChange={(e) => handleFileUpload(e, field.fieldName)}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          style={{ fontSize: "11px", width: "100%" }}
                        />
                        {uploadedFiles[`${field.fieldName}_file`] && (
                          <div style={{ fontSize: "10px", color: "green", marginTop: "2px" }}>
                            ✓ {uploadedFiles[`${field.fieldName}_file`].name}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {field.type === "number" && (
                  <input
                    type="number"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}

                {field.type === "static" && (
                  <div style={{
                    padding: "8px",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "pre-line"
                  }}>
                    {field.value}
                  </div>
                )}

                {field.type === "multiple_fields" && (
                  <div style={{ display: "grid", gap: "8px" }}>
                    {field.fields.map((subField, fieldIndex) => (
                      <div key={fieldIndex} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{
                          display: "block",
                          fontSize: "11px",
                          fontWeight: "bold",
                          marginBottom: "3px",
                          color: "#333"
                        }}>
                          {subField.label}:
                        </label>

                        {subField.type === "textarea" ? (
                          <textarea
                            style={{
                              width: "100%",
                              border: "1px solid #ddd",
                              outline: "none",
                              backgroundColor: "#fff",
                              padding: "6px",
                              fontSize: "12px",
                              borderRadius: "3px"
                            }}
                            rows={subField.rows || 2}
                            value={formData[subField.fieldName] || ""}
                            onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            placeholder={subField.placeholder}
                          />
                        ) // In the multiple_fields rendering, add this condition after the textarea check:
                          : subField.type === "select" ? (
                            <select
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            >
                              <option value="">निवडा</option>
                              {subField.options.map((option, i) => (
                                <option key={i} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          ) : subField.type === "area_unit_dropdown" ? (
                            <select
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            >
                              <option value="">क्षेत्रफळ युनिट निवडा</option>
                              {areaUnits.map((unit) => (
                                <option key={unit.area_unit_id} value={unit.unit_name}>
                                  {unit.unit_name} ({unit.unit_code})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={subField.type}
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                              placeholder={subField.placeholder}
                            />
                          )}
                      </div>
                    ))}
                  </div>
                )}

                {field.type === "multiple_dropdowns" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

                    {/* District */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <label>जिल्हा</label>
                      <select
                        style={{
                          width: "100%",
                          border: "1px solid #ddd",
                          outline: "none",
                          backgroundColor: "#fff",
                          padding: "6px",
                          fontSize: "12px",
                          borderRadius: "3px"
                        }}
                        value={formData.dist_name || ""}
                        onChange={(e) => handleDistrictChange(e.target.value)}
                        disabled={loadingDistricts}
                      >
                        <option value="">{loadingDistricts ? "जिल्हे लोड होत आहेत..." : "जिल्हा निवडा"}</option>
                        {districts.map((district) => (
                          <option key={district.dist_no} value={district.dist_name}>
                            {district.dist_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Taluka */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <label>तालुका</label>
                      <select
                        style={{
                          width: "100%",
                          border: "1px solid #ddd",
                          outline: "none",
                          backgroundColor: "#fff",
                          padding: "6px",
                          fontSize: "12px",
                          borderRadius: "3px"
                        }}
                        value={formData.taluka_name || ""}
                        onChange={(e) => handleTalukaChange(e.target.value)}
                        disabled={loadingTalukas || !formData.dist_name}
                      >
                        <option value="">
                          {!formData.dist_name
                            ? "पहिले जिल्हा निवडा"
                            : loadingTalukas
                              ? "तालुके लोड होत आहेत..."
                              : "तालुका निवडा"}
                        </option>
                        {talukas.map((taluka) => (
                          <option key={taluka.taluka_no} value={taluka.taluka_name}>
                            {taluka.taluka_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Village */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <label>गाव</label>
                      <select
                        style={{
                          width: "100%",
                          border: "1px solid #ddd",
                          outline: "none",
                          backgroundColor: "#fff",
                          padding: "6px",
                          fontSize: "12px",
                          borderRadius: "3px"
                        }}
                        value={formData.village_name || ""}
                        onChange={(e) => handleVillageChange(e.target.value)}
                        disabled={loadingVillages || !formData.dist_name || !formData.taluka_name}
                      >
                        <option value="">
                          {!formData.dist_name || !formData.taluka_name
                            ? "पहिले जिल्हा आणि तालुका निवडा"
                            : loadingVillages
                              ? "गावे लोड होत आहेत..."
                              : "गाव निवडा"}
                        </option>
                        {villages.map((village) => (
                          <option key={village.village_id} value={village.village_name}>
                            {village.village_name}
                          </option>
                        ))}
                      </select>
                    </div>

                  </div>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Save Button */}
      <div style={{
        textAlign: "center",
        marginTop: "20px",
        marginBottom: "20px"
      }}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="no-print"
          style={{
            backgroundColor: saving ? "#ccc" : "#2196F3",
            color: "white",
            padding: "12px 24px",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: "bold"
          }}
        >
          {saving ? <span>{translations[language]?.Updating}</span> : <span>{translations[language]?.Update}</span>}
        </button>
      </div>

      {/* Footer Signatures */}
      <div className="mt-8">
      <p className="text-sm">
      <span className="font-bold text-sm">टिप:- खालील बाबी जोडणे आवश्यक आहे.</span><br />                         
      1. अनुक्रमांक ८ मध्ये उल्लेखिलेल्याप्रमाणे चित्रांकित अनुसूची.<br/>
      2. अनुक्रमांक ९ मध्ये उल्लेखिलेल्याप्रमाणे चित्रांकित/ साक्षांकित नकाशा <br/>
      3. अनुक्रमांक ११ मध्ये उल्लेखिलेल्याप्रमाणे सिमांकन प्रमाणपत्र
      </p>
      </div>
      <div
      style={{
          marginTop: "50px",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "12px",
          alignItems: "flex-start"
      }}
      >
      {/* Left side - Place */}
      <div style={{ width: "40%" }}>
          <strong>ठिकाण :</strong> {formData.village_name || ""}
      </div>

      {/* Right side - Signature block */}
      <div style={{ width: "55%", textAlign: "right" }}>
          <div className="text-center" style={{ marginBottom: "25px" }}>
          संपादन संस्थेच्या जबाबदार अधिकाऱ्याची स्वाक्षरी व शिक्का
          </div>

          <div>
          <p className="text-center">उपकार्यकारी अभियंता, (बांधकाम विभाग),</p>
          <p className="text-center">दक्षिण मध्य रेल्वे, अकोला</p>
          </div>
      </div>
      </div>

      {/* Document Viewer Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {translations[language]?.DocumentPreview}
              </h3>
              <button
                onClick={() => {
                  console.log("❌ Closing modal, current documentUrl:", documentUrl);
                  setShowModal(false);
                  if (documentUrl?.startsWith("blob:")) {
                    console.log("🧹 Revoking blob URL:", documentUrl);
                    URL.revokeObjectURL(documentUrl);
                  }
                }}
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Document Viewer */}
            {console.log("🖼 Rendering iframe with src:", documentUrl)}
            <iframe
              src={documentUrl}
              className="w-full h-[80vh] rounded border border-gray-300"
              title="Document Preview"
            />
          </div>
        </div>
      )}

    </div>

  );
};

// Form fields configuration
const formFields = [
  //1
  {
    label: "प्रस्ताव पाठविणाऱ्या अधिकाऱ्याचे पदनाम व पत्ता",
    type: "dual_text",
    field1Name: "acquisition_incharge_name",
    field1Label: "नाव",
    field1Placeholder: "अधिकाऱ्याचे संपूर्ण नाव",
    field2Name: "acquisition_incharge_designation",
    field2Label: "पदनाम",
    field2Placeholder: "नाव व पदनाम ",
    field3Name: "acquisition_incharge_address",
    field3Label: "पत्ता",
    field3Placeholder: "नाव व पत्ता   ",
  },
  //2
  {
    label: "संपादन संस्थेचे नाव व पत्ता",
    type: "text",
    fieldName: "acquisition_agency",
    placeholder: "आदेश क्रमांक"
  },
  //3
  {
    label: "मंत्रालयीन स्तरावरील संबंधित प्रशासकीय विभाग",
    type: "text",
    fieldName: "acquisition_department",
    placeholder: "विभागाचे नाव"

  },
  //4
  {
    label: "जमीन कोणत्या प्रयोजनासाठी संपादन करून हवी आहे",
    type: "text",
    fieldName: "acquisition_purpose"
  },
  //5
  {
    label: "जमीन संपादन कोणत्या कायदयाखाली करून हवी आहे",
    type: "textarea",
    fieldName: "acquisition_government_policy",
    placeholder: "संस्थेचे संपूर्ण नाव व पत्ता",
    rows: 3
  },
  //6
  {
    label: "संपादन क्षेत्रात मोडणारी गावे, तालुका व जिल्हा कोणता?",
    type: "multiple_dropdowns",
    fields: [
      {
        label: "जिल्हा",
        fieldName: "dist_name",
        type: "district_dropdown"
      },
      {
        label: "तालुका",
        fieldName: "taluka_name",
        type: "taluka_dropdown"
      },
      {
        label: "गाव",
        fieldName: "village_name",
        type: "village_dropdown"
      }
    ]
  },
  //7
  {
    label: "संपादन करून हवे असलेले एकूण क्षेत्र",
    type: "multiple_fields",
    fields: [
      {
        label: "एकूण क्षेत्र",
        fieldName: "total_area",
        type: "text",
        placeholder: "एकूण क्षेत्र प्रविष्ट करा"
      },
      {
        label: "संपादन क्षेत्र",
        fieldName: "aquired_area", // fixed spelling
        type: "text",
        placeholder: "संपादन क्षेत्र प्रविष्ट करा"
      },
      {
        label: "क्षेत्रफळ युनिट",
        fieldName: "area_unit",
        type: "area_unit_dropdown"
      }
    ]
  },
  //8
  {
    label: "गावनिहाय व सर्व क्रमांकानुसार आणि संपादन क्षेत्राच्या चर्तुःसिमा दाखविणारी या सोबतच्या नमून्यातील अनुसूची यासोबत जोडणे आवश्यक आहे त्याप्रमाणे ती जोडली आहे काय?",
    type: "select",
    fieldName: "schedule_6_08",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //9
  {
    label: "संपादन करावयाच्या क्षेत्राचा चित्रांकित साक्षांकित नकाशा यासोबत जोडाणे आवश्यक आहे त्याप्रमाणे तो जोडला आहे काय?",
    type: "select",
    fieldName: "schedule_4_09",
    placeholder: "गावाचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]

  },
  //10
  {
    label: "संपादन करावयाच्या क्षेत्राचे ७/१२ उतारे किंवा मालमत्ता नोंदवहीतील उतारे यासोबत जोडणे आवश्यक आहे त्याप्रमाणे ते जोडले आहेत काय?",
    type: "select",
    fieldName: "extracts_7_12_10",
    placeholder: "तालुक्याचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //11
  {
    label: "संपादन क्षेत्राचे सिमांकन करण्यात आले आहे काय ? असल्यास सिमांकन प्रमाणपत्र सोबत जोडले आहे काय? ",
    type: "select",
    fieldName: "demarcation_11",
    placeholder: "जिल्ह्याचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //12
  {
    label: "संपादन करावयाच्या संपूर्ण जमिनीचा वा तिच्या काही भागाचा खाजगीरित्या ताबा घेतला आहे काय? असल्यास, केव्हा व त्याबाबतीत भुसंपादन नियम पुस्तिकेच्या परिच्छेद २१ अन्वये परिशिष्ट २६ प्रमाणे पंचनामा व करार केला आहे काय? असल्यास, त्याची प्रत सोबत जोडावी.",
    type: "select",
    fieldName: "acquisition_matter_12",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //13
  {
    label: "संपादन कायम स्वरूपाचे किंवा तात्पुरते आहे काय?",
    type: "text",
    fieldName: "land_acquisition_type_13",
    placeholder: "एकूण क्षेत्र"
  },
  //14
  {
    label: "गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे की जरूरीपेक्षा जास्त क्षेत्राची मागणी केली आहे? असल्यास किती जास्त क्षेत्राची व का? ",
    type: "text",
    fieldName: "excess_land_reason_14",
    placeholder: "प्रादेशिक अधिकाऱ्याचे नाव",
  },
  //15
  {
    label: "संबंधित प्रयोजनासाठी जवळपास कमी खर्चाची वा सरकारी जमिन उपलब्ध नाही काय ? असल्यास संपादन करून मागितलेलीच जमीन का हवी आहे? ",
    type: "select",
    fieldName: "other_govt_land_15",
    placeholder: "पदनाम",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //16
  {
    label: "संपादन क्षेत्रात, धार्मिक प्रार्थना स्थळे वा मंदीरे, कबरस्थान वा स्मशानभूमी किंवा प्राचीन स्मारके येतात काय? किंवा ती संपादन क्षेत्राच्या जवळपास असल्यास किती अंतरावर आहेत ?",
    type: "select",
    fieldName: "near_religious_places_16",
    placeholder: "ईमेल पत्ता",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },

  //17
  {
    label: "संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकारी जलप्रवाह, जलवाहिनी किंवा जल:निसारण मार्ग आहे काय किंवा त्यात काही अडथळा निर्माण होतो काय?",
    type: "select",
    fieldName: "public_route_obstruction_17",
    showUpload: true,
    options: [
      { label: "होय", value: "होय" },
      { label: "नाही", value: "नाही" }
    ]
  },
  
  //18
  {
    label: "संपादन करून मागितलेलीच जमीन रेल्वे सिमांच्या जवळपास आहे काय? असल्यास किती अंतरावर आहे.",
    type: "select",
    fieldName: "near_railway_boundary_18",
    placeholder: "प्रकल्पाचे तपशीलवार वर्णन",
    rows: 4,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //19
  {
    label: "नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाच किंवा हवेचे मार्ग, यावरील कोणत्याही अधिकारात अडथळा येण्याचा संभव आहे काय?",
    type: "select",
    fieldName: "road_obstruction_19",
    placeholder: "सामाजिक फायद्याचे वर्णन",
    rows: 3,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //20
  {
    label: "संपादन क्षेत्राची सीमा व इमारत किंवा कारखाना यांना किंवा यांची जागा छेदून आहे काय?",
    type: "select",
    fieldName: "private_buildings_20",
    placeholder: "विस्थापन का आवश्यक आहे",
    rows: 2,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },

  //21
  {
    label: "भुसंपादनाचा प्रस्ताव कोणत्याही निमशासकीय वा शासकीय वा स्थानिक स्वराज्य संस्थेकडून उदा. महानगरपालिका, महामंडळे इत्यादीकरिता असल्यास असे संपादन करण्याबाबतीत आवश्यक तो ठराव मंजूर झाला आहे काय? असल्यास त्याची प्रत जोडावी.",
    type: "textarea",
    fieldName: "institutional_resolution_21"
  },

  // 22
  {
    label: "जर भुसंपादन मध्येच रद्द करण्यात आल्यास तो पर्यंतच्या काळात भुसंपादनाविषयक झालेला सर्व आस्थापना खर्च नियमानुसार देण्याची संपादन संस्थांची तयारी आहे काय? ",
    type: "textarea",
    fieldName: "cancellation_cost_commitment_22"
  },
  // 23
  {
    label: "संपादीत जमिनीची नुकसान भरपाई रक्कम आणि भुसंपादन विषयक सर्व आस्थापना खर्चाची रक्कम यांची आर्थिक तरतूद केलेली आहे काय ? ",
    type: "textarea",
    fieldName: "financial_provision_confirmation_23"
  },
];

export default EditRailwayProposal;